// src/pages/organization/OrgPortalHomePage.jsx
import React from "react";
import {gql, useQuery} from "@apollo/client";
import {useBranding} from "../../context/BrandingContext";
import OrgPortalHeader from "../../components/organization/portal/OrgPortalHeader";
// import OrgPortalHero from "../../components/organization/portal/OrgPortalHero";
import OrgPortalDivisionsSection from "../../components/organization/portal/OrgPortalDivisionsSection";
import OrgPortalUpcomingGamesSection from "../../components/organization/portal/OrgPortalUpcomingGamesSection";
import OrgPortalLatestResultsSection from "../../components/organization/portal/OrgPortalLatestResultsSection";
import OrgPortalFooter from "../../components/organization/portal/OrgPortalFooter";
import VideoSection from "../../components/video/VideoSection";
import Partners from "../../components/organization/portal/Partners";
import {useOrganizationHead} from "../../components/organization/portal/useOrganizationHead";
import OrgPortalBanner from "../../components/organization/portal/OrgPortalBanner";
import useTranslation from "../../i18n/useTranslation";
import OrganizationDivisionSummaryCharts from "../../components/organization/OrganizationDivisionSummaryCharts";

const ORG_PORTAL_HOME = gql`
    query OrgPortalHome {
        orgPortalHome {
            organization {
                id
                name
                slug
                logoUrl
                divisions {
                    id
                    name
                    slug
                    sport {
                        key
                        name
                    }
                    isOpen
                    activeSeason {
                        id
                        name
                        registrationStartDate
                        registrationEndDate
                        startDate
                        endDate
                        state
                        slug
                    }
                    openSeasons {
                        id
                        name
                    }
                    teamsCount
                    playersCount
                }
            }
            upcomingGames {
                id
                title
                eventType
                status
                date
                timezone
                responsibles
                stage {
                    name
                    groups {
                        name
                    }
                }
                season {
                    name
                    division {
                        name
                        organization {
                            name
                        }
                    }
                }
                field {
                    name
                }
                game {
                    noPlay
                    homeTeam {
                        name
                        logoUrl
                    }
                    homeTeamAlias
                    visitorTeam {
                        name
                        logoUrl
                    }
                    visitorTeamAlias
                    group {
                        name
                    }
                    scoreHome
                    scoreVisitor
                    resultType
                }
                trainingSession {
                    teams {
                        team {
                            name
                        }
                    }
                }
            }
            latestResults {
                id
                title
                eventType
                status
                date
                timezone
                responsibles
                stage {
                    name
                    groups {
                        name
                    }
                }
                season {
                    name
                    division {
                        name
                        organization {
                            name
                        }
                    }
                }
                field {
                    name
                }
                game {
                    noPlay
                    homeTeam {
                        name
                        logoUrl
                    }
                    homeTeamAlias
                    visitorTeam {
                        name
                        logoUrl
                    }
                    visitorTeamAlias
                    group {
                        name
                    }
                    scoreHome
                    scoreVisitor
                    resultType
                }
                trainingSession {
                    teams {
                        team {
                            name
                        }
                    }
                }
            }
        }
    }
`;

export default function OrgPortalHomePage() {
    const {branding, theme} = useBranding();
    const {data} = useQuery(ORG_PORTAL_HOME);
    const {language} = useTranslation();

    useOrganizationHead(branding);

    // If branding didn't load (invalid org), BrandingProvider will redirect.
    if (!branding) return null;

    const org = data?.orgPortalHome?.organization;
    const divisions = org?.divisions || [];
    const upcoming = data?.orgPortalHome?.upcomingGames || [];
    const results = data?.orgPortalHome?.latestResults || [];

    const brandingLogoUrl = branding.logoUrl;
    const headerLogo = brandingLogoUrl || org?.logoUrl || theme.logo.main;

    /**
     * 🔹 Adapt global `theme.colors` to the shape the portal components expect.
     * This keeps their API stable (primaryColor, backgroundColor, etc.)
     * while delegating all actual values to the central theme system.
     */
    const colors = theme?.colors || {};

    const safeTheme = {
        // Shell colors
        primaryColor: colors.primary || "#14151A",
        secondaryColor:
            colors.secondary || colors.primary || "#14151A",
        accentColor: colors.accent || "#D5FF5F",

        // Surfaces
        backgroundColor: colors.background || "#05070A",
        surfaceColor: colors.surface || "#0B1220",
        cardBorder: colors.border || "rgba(255,255,255,0.06)",

        // Text tokens
        textOnBackground:
            colors.textOnBackground || "#E5E7EB",
        textOnSurface:
            colors.textOnSurface || "#F9FAFB",
        textOnPrimary:
            colors.textOnPrimary || "#F9FAFB",
        textOnSecondary:
            colors.textOnSecondary ||
            colors.textOnPrimary ||
            "#F9FAFB",
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                fontFamily: "Poppins, sans-serif",
                // 🔹 Use the themed background + text color
                backgroundColor: safeTheme.backgroundColor,
                color: safeTheme.textOnBackground,
            }}
        >
            {/* Header uses theme tokens internally */}
            <OrgPortalHeader org={org} logo={headerLogo} theme={safeTheme}/>

            {/*<OrgPortalBanner/>*/}

            <VideoSection
                key={language}
                src={`/videos/kbs-banner-${language}.mp4`}
                autoPlay
                loop
                muted
                align="center"
                maxWidth="1040px"
                backgroundColor={safeTheme.backgroundColor}
            />

            <div
                style={{
                    maxWidth: 1200,
                    margin: "20px auto",
                    padding: "0 20px",
                }}
            >
                {/*<OrgPortalHero*/}
                {/*    org={org}*/}
                {/*    divisions={divisions}*/}
                {/*    theme={safeTheme}*/}
                {/*/>*/}

                <OrganizationDivisionSummaryCharts
                    organizationSlug={org?.slug}
                />

                <OrgPortalDivisionsSection
                    divisions={divisions}
                    theme={safeTheme}
                />
                <OrgPortalUpcomingGamesSection
                    upcoming={upcoming}
                    theme={safeTheme}
                />
                <OrgPortalLatestResultsSection
                    results={results}
                    theme={safeTheme}
                />

                <Partners theme={safeTheme}/>

                <VideoSection
                    key={language}
                    src={`/videos/kbs-banner-${language}.mp4`}
                    autoPlay
                    loop
                    muted
                    align="center"
                    maxWidth="1040px"
                    background="transparent"
                />
            </div>

            <OrgPortalFooter branding={branding} theme={safeTheme}/>
        </div>
    );
}
